import {
  ProceduresAndDiagnosticsTemplate,
  type ProceduresTemplateProcedure,
} from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import type { RawPractitioner, RawSourceMetadata } from './_shared';
import { getProviderFacilityPdfEntry } from './pdfOverride';

type ProceduresDataKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_DATA; type: DocumentType.PROCEDURES }
>;

interface RawProcedure {
  id: string;
  name?: string;
  dateOfStudy?: string;
  dateUploaded?: string;
  encounterId?: string;
  reason?: string;
  signedUrl?: string;
  asserter?: RawPractitioner;
  performer?: RawPractitioner;
  sourceMetadata?: RawSourceMetadata;
  patientId?: string;
  procedureCodes?: string[];
  procedureNote?: string;
  reasonCodes?: string[];
}

export interface ProceduresDataQueryData {
  procedures: RawProcedure[];
}

const mapProcedure = (p: RawProcedure): ProceduresTemplateProcedure => ({
  id: p.id,
  name: p.name,
  dateOfStudy: p.dateOfStudy,
  reason: p.reason,
  encounterId: p.encounterId,
  asserter: p.asserter,
  performer: p.performer,
  procedureNote: p.procedureNote,
  conditions: [],
  locations: [],
});

export const adaptProceduresData = (
  data: ProceduresDataQueryData,
  key: ProceduresDataKey,
): DocumentEntry | undefined => {
  const procedures = data?.procedures ?? [];
  if (procedures.length === 0) return undefined;

  const pdf = getProviderFacilityPdfEntry(key.sourceType, procedures, {
    title: key.name,
  });
  if (pdf) return pdf;

  const mapped = procedures.map(mapProcedure);

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.procedureId
      ? `procedure-${key.procedureId}`
      : undefined,
    render: (ctx) => (
      <ProceduresAndDiagnosticsTemplate
        procedures={mapped}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightProcedureId={key.procedureId}
      />
    ),
  };
};
